import { useTranslation } from "react-i18next";

import { useLocale } from "@/hooks/useLocale";
import type { ILopDetail } from "@/types/(company)/payrollRuns.interface";
import { cn } from "@/utils/cn";
import { groupLopDates } from "./lop-dates";

/**
 * The days behind a Loss of Pay amount (B45): "9 days: Jun 2, 15, 17–19 …".
 * Payslips generated before the dates were kept show the count alone.
 */
export const LopDetailNote = ({
  detail,
  className,
}: {
  detail: ILopDetail | null | undefined;
  className?: string;
}) => {
  const { t } = useTranslation("company");
  const { formatDate, formatNumber } = useLocale();
  if (!detail || !(detail.days > 0 || detail.dates.length > 0)) return null;

  const dates = groupLopDates(detail.dates)
    .map((m) =>
      t("payslip.lop_month", {
        month: formatDate(m.monthDate, "LLL"),
        days: m.runs
          .map((r) =>
            r.half
              ? t("payslip.lop_half_day", { day: formatNumber(r.from) })
              : r.from === r.to
                ? formatNumber(r.from)
                : t("payslip.lop_range", {
                    from: formatNumber(r.from),
                    to: formatNumber(r.to),
                  }),
          )
          .join(t("payslip.lop_separator")),
      }),
    )
    .join(t("payslip.lop_month_separator"));

  const count = detail.days;
  const text = !detail.dates.length
    ? t("payslip.lop_days_count", { count })
    : detail.adjusted
      ? t("payslip.lop_days_adjusted", { count, dates })
      : t("payslip.lop_days_dates", { count, dates });

  return (
    <span className={cn("text-[10px] text-muted-foreground", className)}>
      {text}
    </span>
  );
};
